/**
 * **内核自带的节点** —— 编辑器自己的结构与通用计算。
 *
 * ⚠️ 这一批原来是和领域节点混在一起的（都在 `playground/nodeTypes.ts`）。
 * 拆出来的判据是：**内核自己的代码要在类型上分支的，节点就必须在内核里。**
 *
 *  · `useSubgraphLevels` 判 `'group'` / `'loop'`（进子图、找进出端口）
 *  · `useGrouping` / `frame.ts` / `loop.ts` / `reroute.ts` 各自处理框、循环、接通
 *  · 这些构造**任何**节点编辑器都要有（Blender 的 node group 就是一个）
 *
 * 「把打印两个字删掉，这些节点还要不要？」—— 要。所以它们在内核。
 *
 * ⚠️ `function.*`（or / if / switch / filter / list.count）也在这里：
 * 它们是**纯数据运算**，不含任何印刷语义（没有纸张、条码、面单）。
 * 判据与例外见 `docs/node-packages.md`。
 */
import { DEFAULT_FRAME_HEIGHT, DEFAULT_FRAME_WIDTH } from '../types/node'
import { DEFAULT_LOOP_SLOTS, loopExternalRows } from './loop'
import { num } from './nodeRows'
import type { NodeTypeDef } from './package'
import { NODE_ICONS } from './nodeIcons'
import { JS_NODE_TYPE } from './jsNodeType'
import type { NodeRow } from '../types/node'

function rowsDebug(): NodeRow[] {
  return [
    {
      // socketType: 'any' → 任何类型都能连进来
      kind: 'repeater',
      id: 'watch',
      label: '监听',
      socketType: 'any',
      min: 1,
      max: 12,
    },
    {
      // 不设 height → 高度自适应，接的值越多面板越高
      kind: 'display',
      id: 'result',
      label: '结果',
      render: 'debug',
    },
  ]
}

function rowsEmpty(): NodeRow[] {
  return []
}

/**
 * 过滤列表。
 *
 * 「字段」留空就直接拿元素本身比（list<number> / list<text>），
 * 填了就按属性读（list<object>），支持 `a.b` 路径。
 */
function rowsFrame(): NodeRow[] {
  return []
}

/**
 * 组节点。
 *
 * 它自己的 rows 是**接口**（外面看到的端口）—— 由「用选中节点建组」
 * 从跨边界的边推出来，见 node/group.ts 的 planGrouping。
 * 手动从菜单拖出来时是一张空组（没有端口）。
 */
function rowsGroup(): NodeRow[] {
  return []
}

function rowsGroupInput(): NodeRow[] {
  return []
}

function rowsGroupOutput(): NodeRow[] {
  return []
}

/**
 * 循环节点。
 *
 * 它自己的 rows 是**外部接口**（列表 / 步进 + N 路 list 输出）——
 * 由 loopExternalRows 按槽数生成。
 */
function rowsLoop(): NodeRow[] {
  return loopExternalRows(DEFAULT_LOOP_SLOTS)
}

function rowsNote(): NodeRow[] {
  return []
}


/**
 * 接通节点：两端都是 `any`。
 *
 * 它由「拆开一条已有的边」产生，所以两端的类型本来就是兼容的 ——
 * 用 `any` 让它可以承载任何东西，不必跟着上游变色。
 */
function rowsReroute(): NodeRow[] {
  return [
    { kind: 'input', id: 'in', label: '', socket: { id: 'in', type: 'any' } },
    { kind: 'output', id: 'out', label: '', socket: { id: 'out', type: 'any' } },
  ]
}


export const CORE_NODE_TYPES: NodeTypeDef[] = [
  /*
   * ★ **JS 节点**（`core.js`）：用户带一段 JS + 自己声明输入输出。
   * 定义在 `jsNodeType.ts` 里（那个文件还带 runner 与端口声明的解析），
   * 这里只是把它挂进内核目录。
   */
  JS_NODE_TYPE,
  {
    // ── 循环：拆列表、每项跑一次子图、收集成 list ──
    type: 'loop',
    icon: NODE_ICONS['loop'],
    title: '循环',
    category: 'utility',
    width: 200,
    hint: '拆开列表，每一项跑一次内部子图，把结果收集成新的 list',
    keywords: ['loop', 'for', 'map', '循环', '遍历', '列表'],
    rows: rowsLoop(),
  },
  {
    // 循环内部的两个固定节点（不进添加菜单）
    type: 'loop.start',
    icon: NODE_ICONS['loop.start'],
    title: '循环起始',
    category: 'utility',
    width: 150,
    hidden: true,
    hint: '循环内部：每一轮的当前项 / 序号 / 外部参数',
    keywords: ['start', '起始', '循环'],
    rows: rowsEmpty(),
  },
  {
    type: 'loop.output',
    icon: NODE_ICONS['loop.output'],
    title: '结果累记',
    category: 'utility',
    width: 150,
    hidden: true,
    hint: '循环内部：每一路接出去就是一个 list 结果',
    keywords: ['accumulate', '累记', '结果'],
    rows: rowsEmpty(),
  },
  {
    // ── 组：一次性封装的子图 ──
    type: 'group',
    icon: NODE_ICONS['group'],
    title: '组',
    category: 'utility',
    width: 200,
    hint: '把一组节点收进一个节点里，用来整理版面',
    keywords: ['group', '组', '封装', '打包', '子图'],
    rows: rowsGroup(),
  },
  {
    /*
     * 子图内部的两个接口节点。
     *
     * `hidden` 让它们不出现在添加菜单里；`editorOnly` **不能**设 ——
     * 它们要求值（值来自 seeds 和内部连线）。
     * 「不可删」由建组逻辑和删除路径保证（见 Playground）。
     */
    type: 'group.input',
    icon: NODE_ICONS['group.input'],
    title: '输入',
    category: 'utility',
    width: 140,
    hidden: true,
    hint: '组内部：外部喂进来的参数，逐个展开成输出',
    keywords: ['input', '输入', '接口'],
    rows: rowsGroupInput(),
  },
  {
    type: 'group.output',
    icon: NODE_ICONS['group.output'],
    title: '输出',
    category: 'utility',
    width: 140,
    hidden: true,
    hint: '组内部：接几路，组对外就显示几个结果端口',
    keywords: ['output', '输出', '接口'],
    rows: rowsGroupOutput(),
  },
  {
    // ── 框：把节点圈起来，拖动时带着成员一起走 ──
    type: 'frame',
    icon: NODE_ICONS['frame'],
    title: '框',
    category: 'utility',
    width: DEFAULT_FRAME_WIDTH,
    height: DEFAULT_FRAME_HEIGHT,
    render: 'frame',
    editorOnly: true,
    hint: '把节点圈起来，拖动时带着它们一起走（不参与输出）',
    keywords: ['frame', '框', '分组', '圈', 'group', 'box'],
    rows: rowsFrame(),
  },
  {
    // ── 注释：一块能写多行文字的区域，做说明用 ──
    type: 'note',
    icon: NODE_ICONS['note'],
    title: '注释',
    category: 'utility',
    width: 220,
    height: 120,
    render: 'note',
    editorOnly: true,
    hint: '在版面上写备注（不参与输出）',
    keywords: ['note', '注释', '说明', '标签', '备注', 'comment', 'label'],
    rows: rowsNote(),
  },
  {
    // ── 接通（重路由）：一个点，只有左右两个端口 ──
    type: 'reroute',
    icon: NODE_ICONS['reroute'],
    title: '接通',
    category: 'utility',
    // 圆点很小，宽度只是一个命中热区
    width: 18,
    render: 'dot',
    // 双击线条创建，不进添加菜单
    hidden: true,
    hint: '接通 / 重路由：让连线拐个弯，求值时直通',
    keywords: ['reroute', '接通', '重路由', '接线', '布线'],
    rows: rowsReroute(),
  },
  {
    type: 'debug.viewer',
    icon: NODE_ICONS['debug.viewer'],
    title: '调试 · 输出',
    category: 'utility',
    width: 258,
    hint: '把接进来的值显示出来，方便看中间结果',
    keywords: ['debug', 'viewer', '调试', '查看'],
    rows: rowsDebug(),
  },
]
